/**
 * Documents — show view on the shared Inspector grammar (coverage §2.1).
 * Header / Overview / Body / Relations / Audit, with the two named primary
 * actions (both real PATCHes on [EXISTS] columns):
 *   - **mark canonical** → `PATCH /api/documents/:id { canonical_status }`.
 *   - **set reviewed**   → `PATCH /api/documents/:id { last_reviewed_at }`.
 *
 * The agent-readable summary renders as the Body block; the source URL is a
 * quiet external link in Overview.
 */
import {
  useShow,
  useGo,
  useNavigation,
  useUpdate,
  type HttpError,
} from "@refinedev/core";
import {
  Pencil,
  BadgeCheck,
  CalendarCheck,
  FolderKanban,
  UserRound,
  ExternalLink as ExternalLinkIcon,
} from "@/components/ui/icon";
import type { Document } from "@aos/shared";

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Inspector,
  InspectorHeader,
  InspectorSection,
  ShowPageSkeleton,
  ShowPageError,
  TechnicalId,
  FieldGrid,
  Field,
  RefLink,
  RelationsBlock,
  AuditBlock,
} from "@/components/inspector";
import { Markdown } from "@/components/app/MarkdownView";
import { humanize } from "../projects/_form";

export function DocumentShow() {
  const go = useGo();
  const { editUrl } = useNavigation();
  const { query } = useShow<Document, HttpError>({ resource: "documents" });
  const { data, isLoading, isError, error, refetch } = query;
  const doc = data?.data;
  const { mutate: updateDoc, isLoading: updating } = useUpdate();

  if (isLoading) return <ShowPageSkeleton />;
  if (isError || !doc) {
    return (
      <ShowPageError
        message={error?.message}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  const isCanonical = doc.canonical_status === "canonical";
  const markCanonical = () =>
    updateDoc({
      resource: "documents",
      id: doc.id,
      values: {
        canonical_status: isCanonical ? "active" : "canonical",
      },
      successNotification: {
        type: "success",
        message: isCanonical ? "Unmarked as canonical" : "Marked canonical",
      },
    });

  const setReviewed = () =>
    updateDoc({
      resource: "documents",
      id: doc.id,
      values: { last_reviewed_at: new Date().toISOString() },
      successNotification: {
        type: "success",
        message: "Marked reviewed",
      },
    });

  return (
    <Inspector>
      <InspectorHeader
        title={doc.title}
        subtitle="An external document registered in the operating core."
        status={doc.canonical_status}
        primaryAction={
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button disabled={updating}>Actions</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={markCanonical}>
                <BadgeCheck className="mr-2 h-4 w-4" />
                {isCanonical ? "Unmark canonical" : "Mark canonical"}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={setReviewed}>
                <CalendarCheck className="mr-2 h-4 w-4" /> Set reviewed now
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        }
        actions={
          <>
            {doc.external_url ? (
              <Button variant="outline" asChild>
                <a
                  href={doc.external_url}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  <ExternalLinkIcon className="mr-2 h-4 w-4" /> Source
                </a>
              </Button>
            ) : null}
            <Button
              variant="outline"
              onClick={() => go({ to: editUrl("documents", doc.id) })}
            >
              <Pencil className="mr-2 h-4 w-4" /> Edit
            </Button>
          </>
        }
      />

      <InspectorSection title="Overview">
        <FieldGrid>
          <Field label="Canonical status">
            {humanize(doc.canonical_status)}
          </Field>
          <Field label="Type">
            {doc.document_type ? humanize(doc.document_type) : null}
          </Field>
          <Field label="Confidentiality">
            {humanize(doc.confidentiality)}
          </Field>
          <Field label="Source system">{doc.source_system}</Field>
          <Field label="Project">
            <RefLink resource="projects" id={doc.project_id} />
          </Field>
          <Field label="Owner">
            <RefLink resource="users" id={doc.owner_user_id} />
          </Field>
          <Field label="External URL" full>
            {doc.external_url ? (
              <a
                href={doc.external_url}
                target="_blank"
                rel="noreferrer noopener"
                className="break-all text-primary hover:underline"
              >
                {doc.external_url}
              </a>
            ) : null}
          </Field>
          <Field label="Last reviewed">
            {doc.last_reviewed_at
              ? new Date(doc.last_reviewed_at).toLocaleString()
              : null}
          </Field>
          <Field label="Created">
            {new Date(doc.created_at).toLocaleString()}
          </Field>
          <Field label="Updated">
            {new Date(doc.updated_at).toLocaleString()}
          </Field>
        </FieldGrid>
      </InspectorSection>

      {doc.agent_summary ? (
        <InspectorSection title="Body">
          <Markdown source={doc.agent_summary} />
        </InspectorSection>
      ) : null}

      <InspectorSection title="Relations">
        <RelationsBlock
          groups={[
            {
              title: "References",
              items: [
                {
                  key: "project",
                  label: "Project",
                  icon: FolderKanban,
                  node: <RefLink resource="projects" id={doc.project_id} />,
                },
                {
                  key: "owner",
                  label: "Owner",
                  icon: UserRound,
                  node: <RefLink resource="users" id={doc.owner_user_id} />,
                },
              ],
            },
          ]}
        />
      </InspectorSection>

      <InspectorSection title="Technical">
        <FieldGrid className="text-muted-foreground">
          <Field label="Id">
            <TechnicalId id={doc.id} />
          </Field>
        </FieldGrid>
      </InspectorSection>

      <InspectorSection title="Audit">
        <AuditBlock entityType="documents" entityId={doc.id} />
      </InspectorSection>
    </Inspector>
  );
}

export default DocumentShow;
